import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import "./AdminAuraPortal.css";
import auraWorldMapBg from "../../assets/aura-world-map-bg.png";
import { AURA_GEOGRAPHY } from "../../data/auraGeography";
import ErhAlertPopup from "../AuraERH/ErhAlertPopup";

type ViewMode = "district" | "school" | "region" | "nation";
type RangeLabel = "7" | "30" | "90" | "365";
type SeverityTone = "low" | "moderate" | "elevated" | "critical";

type AuraSummaryResp = {
  active_signals?: number;
  flagged_schools?: number;
  wellness_engagement?: number;
  crisis_watch?: string;
  emotional_climate?: string;
  advisor_response?: number;
  score?: number;
};

type AuraSchoolItem = {
  school_name?: string;
  status?: string;
  score?: number;
  events?: number;
  country?: string;
  region?: string;
  district?: string;
  org_code?: string;
  school_id?: string | number;
};

type AuraFeedItem = {
  id?: number;
  created_at?: string;
  school_name?: string;
  category?: string;
  trigger?: string;
  severity?: string;
  signal_label?: string;
  signal_category?: string;
  country?: string;
  region?: string;
  district?: string;
  user_role?: string;
};

type AuraOrgItem = {
  id?: number;
  name?: string;
  org_name?: string;
  school_name?: string;
  country?: string;
  region?: string;
  district?: string;
};

type NormalizedSchool = {
  school_name: string;
  status: SeverityTone;
  score: number;
  events: number;
  country?: string;
  region?: string;
  district?: string;
};

type BucketRow = {
  label: string;
  fullLabel: string;
  score: number;
  events: number;
  critical: number;
  high: number;
};

type SparklinePaths = {
  score: string;
  events: string;
  critical: string;
};

function safeNum(v: unknown, d = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function clamp(n: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, n));
}

function cleanText(v: unknown): string {
  return String(v || "").trim();
}

function safeDisplayText(value: any): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(safeDisplayText).filter(Boolean).join("; ");
  if (typeof value === "object") {
    if (typeof value.msg === "string") return value.msg;
    if (typeof value.detail === "string") return value.detail;
    if (Array.isArray(value.detail)) return safeDisplayText(value.detail);
    try {
      return JSON.stringify(value);
    } catch {
      return "Unexpected error";
    }
  }
  return String(value);
}

function titleCase(v: unknown): string {
  return String(v || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeSeverity(v?: string): SeverityTone {
  const s = String(v || "").trim().toLowerCase();
  if (s === "critical") return "critical";
  if (s === "elevated" || s === "high") return "elevated";
  if (s === "moderate" || s === "medium") return "moderate";
  return "low";
}

function statusFromScore(score: number): SeverityTone {
  const s = clamp(Math.round(score));
  if (s >= 90) return "critical";
  if (s >= 70) return "elevated";
  if (s >= 45) return "moderate";
  return "low";
}

function severityClass(v?: string): SeverityTone {
  return normalizeSeverity(v);
}

function schoolToneClass(v?: string): SeverityTone {
  return normalizeSeverity(v);
}

function fmtPercent(v: unknown): string {
  return `${Math.round(safeNum(v, 0))}%`;
}

function fmtShortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtMonth(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short" });
}

function scoreToStormProbability(score: number): number {
  return clamp(Math.round(score), 0, 99);
}

function severityWeight(severity?: string): number {
  const sev = normalizeSeverity(severity);
  if (sev === "critical") return 90;
  if (sev === "elevated") return 70;
  if (sev === "moderate") return 45;
  return 15;
}

function canonicalSchoolKey(v?: string): string {
  const raw = String(v || "").trim().toLowerCase();
  if (!raw || raw.includes("unknown")) return "";
  return raw.replace(/[^a-z0-9]/g, "");
}

function normalizeDisplaySchoolName(v?: string): string {
  const raw = String(v || "").trim();
  if (!raw || raw.toLowerCase().includes("unknown")) return "";
  return raw;
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.map(cleanText).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
}

function getAllContinents(): string[] {
  return AURA_GEOGRAPHY.map((c) => c.continent);
}

function getRegionsByContinent(continent: string): string[] {
  if (continent === "ALL") {
    return uniqueSorted(AURA_GEOGRAPHY.flatMap((c) => c.regions.map((r) => r.name)));
  }

  const match = AURA_GEOGRAPHY.find((c) => c.continent === continent);
  return match ? uniqueSorted(match.regions.map((r) => r.name)) : [];
}

function getCountriesByRegion(region: string): string[] {
  for (const continent of AURA_GEOGRAPHY) {
    for (const reg of continent.regions) {
      if (reg.name === region) return reg.countries;
    }
  }
  return [];
}

function getCountriesByContinent(continent: string): string[] {
  if (continent === "ALL") {
    return uniqueSorted(
      AURA_GEOGRAPHY.flatMap((c) => c.regions.flatMap((r) => r.countries))
    );
  }

  const match = AURA_GEOGRAPHY.find((c) => c.continent === continent);
  return match ? uniqueSorted(match.regions.flatMap((r) => r.countries)) : [];
}

function getGeoForCountry(country?: string): { continent: string; region: string } {
  const target = cleanText(country);
  if (!target) return { continent: "", region: "" };

  for (const continent of AURA_GEOGRAPHY) {
    for (const region of continent.regions) {
      if (region.countries.includes(target)) {
        return { continent: continent.continent, region: region.name };
      }
    }
  }

  return { continent: "", region: "" };
}

function parseDate(v?: string): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function addMonths(d: Date, n: number): Date {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86400000);
}

function rangeDays(rangeLabel: RangeLabel): number {
  if (rangeLabel === "365") return 365;
  if (rangeLabel === "90") return 90;
  if (rangeLabel === "30") return 30;
  return 7;
}

function rangeText(rangeLabel: RangeLabel): string {
  if (rangeLabel === "365") return "Past 12 Months";
  if (rangeLabel === "90") return "Past 90 Days";
  if (rangeLabel === "30") return "Past 30 Days";
  return "Past 7 Days";
}

function buildDateBuckets(feed: AuraFeedItem[], rangeLabel: RangeLabel): BucketRow[] {
  const validDates = feed.map((f) => parseDate(f.created_at)).filter((d): d is Date => Boolean(d));

  const today = startOfDay(new Date());
  const end = validDates.length
    ? startOfDay(new Date(Math.max(...validDates.map((d) => d.getTime()))))
    : today;

  if (rangeLabel === "365") {
    const firstMonth = addMonths(end, -11);

    const buckets: BucketRow[] = Array.from({ length: 12 }).map((_, idx) => {
      const bucketDate = addMonths(end, idx - 11);
      return {
        label: fmtMonth(bucketDate),
        fullLabel: bucketDate.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
        score: 0,
        events: 0,
        critical: 0,
        high: 0,
      };
    });

    feed.forEach((item) => {
      const d = parseDate(item.created_at);
      if (!d) return;

      const bucketIdx =
        (d.getFullYear() - firstMonth.getFullYear()) * 12 +
        (d.getMonth() - firstMonth.getMonth());

      if (bucketIdx < 0 || bucketIdx >= buckets.length) return;

      const sev = severityClass(item.severity);
      const row = buckets[bucketIdx];

      row.events += 1;
      row.score += severityWeight(item.severity);

      if (sev === "critical") row.critical += 1;
      if (sev === "elevated") row.high += 1;
    });

    buckets.forEach((b) => {
      b.score = clamp(b.score);
    });

    return buckets;
  }

  const bucketSize = rangeLabel === "90" ? 7 : 1;
  const bucketCount = rangeLabel === "90" ? 13 : rangeDays(rangeLabel);
  const start = addDays(end, -(bucketCount * bucketSize - 1));

  const buckets: BucketRow[] = Array.from({ length: bucketCount }).map((_, idx) => {
    const bucketStart = addDays(start, idx * bucketSize);
    const bucketEnd = addDays(bucketStart, bucketSize - 1);

    return {
      label:
        bucketSize === 1
          ? fmtShortDate(bucketStart)
          : `${fmtShortDate(bucketStart)}-${fmtShortDate(bucketEnd)}`,
      fullLabel:
        bucketSize === 1
          ? fmtShortDate(bucketStart)
          : `${fmtShortDate(bucketStart)} - ${fmtShortDate(bucketEnd)}`,
      score: 0,
      events: 0,
      critical: 0,
      high: 0,
    };
  });

  feed.forEach((item) => {
    const d = parseDate(item.created_at);
    const bucketIdx = d ? Math.floor(daysBetween(start, d) / bucketSize) : buckets.length - 1;

    if (bucketIdx < 0 || bucketIdx >= buckets.length) return;

    const sev = severityClass(item.severity);
    const row = buckets[bucketIdx];

    row.events += 1;
    row.score += severityWeight(item.severity);

    if (sev === "critical") row.critical += 1;
    if (sev === "elevated") row.high += 1;
  });

  buckets.forEach((b) => {
    b.score = clamp(b.score);
  });

  return buckets;
}

function buildYAxisTicks(max: number): number[] {
  const safeMax = Math.max(1, Math.ceil(max));

  const step =
    safeMax <= 10 ? 2 :
    safeMax <= 50 ? 10 :
    safeMax <= 100 ? 20 :
    safeMax <= 500 ? 100 :
    safeMax <= 1000 ? 200 :
    Math.ceil(safeMax / 5 / 100) * 100;

  const top = Math.ceil(safeMax / step) * step;

  return [top, top - step, top - step * 2, top - step * 3, 0].filter(
    (v, idx, arr) => v >= 0 && arr.indexOf(v) === idx
  );
}

function buildSparklinePaths(rows: BucketRow[], chartMax: number, width: number, height = 170): SparklinePaths {
  if (!rows.length) return { score: "", events: "", critical: "" };

  const stepX = rows.length > 1 ? width / (rows.length - 1) : width;

  function makePath(getter: (row: BucketRow) => number): string {
    return rows
      .map((row, idx) => {
        const x = idx * stepX;
        const rawValue = getter(row);
        const y = height - (rawValue / chartMax) * (height - 20) - 10;
        return `${idx === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }

  return {
    score: makePath((r) => r.score),
    events: makePath((r) => r.events),
    critical: makePath((r) => r.critical + r.high),
  };
}

export default function AdminAuraPortal() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [summary, setSummary] = useState<AuraSummaryResp>({});
  const [schools, setSchools] = useState<AuraSchoolItem[]>([]);
  const [feed, setFeed] = useState<AuraFeedItem[]>([]);
  const [orgs, setOrgs] = useState<AuraOrgItem[]>([]);

  const [viewMode, setViewMode] = useState<ViewMode>("district");
  const [rangeLabel, setRangeLabel] = useState<RangeLabel>("365");

  const [selectedContinent, setSelectedContinent] = useState("ALL");
  const [selectedRegion, setSelectedRegion] = useState("ALL");
  const [selectedCountry, setSelectedCountry] = useState("ALL");
  const [selectedDistrict, setSelectedDistrict] = useState("ALL");
  const [selectedSchool, setSelectedSchool] = useState("ALL");

  useEffect(() => {
    let alive = true;

    async function loadAura() {
      setLoading(true);
      setErr("");

      try {
        const days = rangeDays(rangeLabel);

        const summaryParams = { days, limit: 100 };
        const schoolsParams = { days, limit: 100 };
        const feedParams = { days, limit: 100 };

        const [summaryRes, schoolsRes, feedRes, orgsRes] = await Promise.all([
          api.get<AuraSummaryResp>("/admin/aura/summary", { params: summaryParams }),
          api.get<{ items?: AuraSchoolItem[] }>("/admin/aura/schools", { params: schoolsParams }),
          api.get<{ items?: AuraFeedItem[] }>("/admin/aura/feed", { params: feedParams }),
          api.get<{ items?: AuraOrgItem[] }>("/admin/organizations"),
        ]);

        if (!alive) return;

        setSummary(summaryRes.data || {});
        setSchools(Array.isArray(schoolsRes.data?.items) ? schoolsRes.data.items : []);
        setFeed(Array.isArray(feedRes.data?.items) ? feedRes.data.items : []);
        setOrgs(Array.isArray(orgsRes.data?.items) ? orgsRes.data.items : []);
      } catch (e: any) {
        if (!alive) return;

        setErr(
          safeDisplayText(
            e?.response?.data?.detail ||
              e?.response?.data ||
              e?.message ||
              "Failed to load AURA data."
          )
        );
      } finally {
        if (alive) setLoading(false);
      }
    }

    void loadAura();

    return () => {
      alive = false;
    };
  }, [rangeLabel]);

  const orgGeoBySchool = useMemo(() => {
    const map = new Map<string, { country: string; region: string; district: string }>();

    orgs.forEach((org) => {
      const schoolName = cleanText(org.org_name || org.name || org.school_name);
      const key = canonicalSchoolKey(schoolName);

      if (!key) return;

      map.set(key, {
        country: cleanText(org.country),
        region: cleanText(org.region),
        district: cleanText(org.district),
      });
    });

    return map;
  }, [orgs]);

  const normalizedFeed = useMemo(
    () =>
      feed.map((item) => {
        const schoolName = normalizeDisplaySchoolName(item.school_name);
        const orgGeo = orgGeoBySchool.get(canonicalSchoolKey(schoolName));

        return {
          ...item,
          school_name: schoolName,
          country: cleanText(item.country) || orgGeo?.country || "",
          region: cleanText(item.region) || orgGeo?.region || "",
          district: cleanText(item.district) || orgGeo?.district || "",
        };
      }),
    [feed, orgGeoBySchool]
  );

  const cleanSchools = useMemo((): NormalizedSchool[] => {
    const merged = new Map<string, NormalizedSchool>();

    schools.forEach((s) => {
      const rawName = cleanText(s.school_name);
      if (!rawName || rawName.toLowerCase().includes("unknown")) return;

      const normalizedName = normalizeDisplaySchoolName(rawName);
      const mergeKey = canonicalSchoolKey(normalizedName);

      if (!normalizedName || !mergeKey) return;

      const orgGeo = orgGeoBySchool.get(mergeKey);
      const country = cleanText(s.country) || orgGeo?.country || "";
      const inferredGeo = getGeoForCountry(country);
      const region = cleanText(s.region) || orgGeo?.region || inferredGeo.region;
      const district = cleanText(s.district) || orgGeo?.district || "";
      const score = clamp(safeNum(s.score, 0));
      const status = normalizeSeverity(s.status) || statusFromScore(score);

      const nextSchool: NormalizedSchool = {
        school_name: normalizedName,
        status: statusFromScore(score) === "low" ? status : statusFromScore(score),
        score,
        events: safeNum(s.events, 0),
        country,
        region,
        district,
      };

      const existing = merged.get(mergeKey);

      if (!existing) {
        merged.set(mergeKey, nextSchool);
        return;
      }

      const rank: Record<SeverityTone, number> = {
        low: 0,
        moderate: 1,
        elevated: 2,
        critical: 3,
      };

      merged.set(mergeKey, {
        school_name: existing.school_name || nextSchool.school_name,
        status: rank[nextSchool.status] > rank[existing.status] ? nextSchool.status : existing.status,
        score: Math.max(existing.score, nextSchool.score),
        events: existing.events + nextSchool.events,
        country: existing.country || nextSchool.country,
        region: existing.region || nextSchool.region,
        district: existing.district || nextSchool.district,
      });
    });

    return Array.from(merged.values()).sort((a, b) => b.score - a.score);
  }, [schools, orgGeoBySchool]);

  const continentOptions = useMemo(() => getAllContinents(), []);

  const regionOptions = useMemo(
    () => getRegionsByContinent(selectedContinent),
    [selectedContinent]
  );

  const countryOptions = useMemo(() => {
    if (selectedRegion !== "ALL") {
      return uniqueSorted(getCountriesByRegion(selectedRegion));
    }

    return getCountriesByContinent(selectedContinent);
  }, [selectedContinent, selectedRegion]);

  const districtOptions = useMemo(() => {
    return uniqueSorted(
      cleanSchools
        .filter((s) => {
          const geo = getGeoForCountry(s.country);
          const schoolContinent = geo.continent;
          const schoolRegion = cleanText(s.region) || geo.region;

          if (selectedContinent !== "ALL" && schoolContinent !== selectedContinent) return false;
          if (selectedRegion !== "ALL" && schoolRegion !== selectedRegion) return false;
          if (selectedCountry !== "ALL" && s.country !== selectedCountry) return false;

          return true;
        })
        .map((s) => s.district || "")
    );
  }, [cleanSchools, selectedContinent, selectedRegion, selectedCountry]);

  const schoolOptions = useMemo(() => {
    return uniqueSorted(
      cleanSchools
        .filter((s) => {
          const geo = getGeoForCountry(s.country);
          const schoolContinent = geo.continent;
          const schoolRegion = cleanText(s.region) || geo.region;

          if (selectedContinent !== "ALL" && schoolContinent !== selectedContinent) return false;
          if (selectedRegion !== "ALL" && schoolRegion !== selectedRegion) return false;
          if (selectedCountry !== "ALL" && s.country !== selectedCountry) return false;
          if (selectedDistrict !== "ALL" && s.district !== selectedDistrict) return false;

          return true;
        })
        .map((s) => s.school_name || "")
    );
  }, [cleanSchools, selectedContinent, selectedRegion, selectedCountry, selectedDistrict]);

  const filteredSchools = useMemo(() => {
    return cleanSchools.filter((s) => {
      const geo = getGeoForCountry(s.country);
      const schoolContinent = geo.continent;
      const schoolRegion = cleanText(s.region) || geo.region;

      if (selectedContinent !== "ALL" && schoolContinent !== selectedContinent) return false;
      if (selectedRegion !== "ALL" && schoolRegion !== selectedRegion) return false;
      if (selectedCountry !== "ALL" && cleanText(s.country) !== selectedCountry) return false;
      if (selectedDistrict !== "ALL" && cleanText(s.district) !== selectedDistrict) return false;
      if (selectedSchool !== "ALL" && canonicalSchoolKey(s.school_name) !== canonicalSchoolKey(selectedSchool)) return false;

      return true;
    });
  }, [cleanSchools, selectedContinent, selectedRegion, selectedCountry, selectedDistrict, selectedSchool]);

  const hasActiveGeoFilter =
    selectedContinent !== "ALL" ||
    selectedRegion !== "ALL" ||
    selectedCountry !== "ALL" ||
    selectedDistrict !== "ALL" ||
    selectedSchool !== "ALL";

  const visibleSchools = useMemo(() => {
    if (filteredSchools.length) return filteredSchools;
    if (hasActiveGeoFilter) return [];
    return cleanSchools;
  }, [filteredSchools, hasActiveGeoFilter, cleanSchools]);

  const topSchool = useMemo(
    () =>
      visibleSchools[0] || {
        school_name: "No Data",
        status: "low" as SeverityTone,
        score: 0,
        events: 0,
      },
    [visibleSchools]
  );

  const filteredFeed = useMemo(() => {
    return normalizedFeed.filter((f) => {
      const schoolName = cleanText(f.school_name);
      if (!schoolName || schoolName.toLowerCase().includes("unknown")) return false;

      const geo = getGeoForCountry(f.country);
      const feedContinent = geo.continent;
      const feedRegion = cleanText(f.region) || geo.region;

      if (selectedContinent !== "ALL" && feedContinent !== selectedContinent) return false;
      if (selectedRegion !== "ALL" && feedRegion !== selectedRegion) return false;
      if (selectedCountry !== "ALL" && cleanText(f.country) !== selectedCountry) return false;
      if (selectedDistrict !== "ALL" && cleanText(f.district) !== selectedDistrict) return false;
      if (selectedSchool !== "ALL" && canonicalSchoolKey(f.school_name) !== canonicalSchoolKey(selectedSchool)) return false;

      return true;
    });
  }, [normalizedFeed, selectedContinent, selectedRegion, selectedCountry, selectedDistrict, selectedSchool]);

  const chartFeed = useMemo(() => {
    if (filteredFeed.length) return filteredFeed;
    if (hasActiveGeoFilter) return [];

    return normalizedFeed.filter(
      (f) => canonicalSchoolKey(f.school_name) === canonicalSchoolKey(topSchool.school_name)
    );
  }, [filteredFeed, hasActiveGeoFilter, normalizedFeed, topSchool.school_name]);

  const chartRows = useMemo(() => buildDateBuckets(chartFeed, rangeLabel), [chartFeed, rangeLabel]);

  const chartMax = useMemo(() => {
    const values = chartRows.map((t) => Math.max(t.score, t.events, t.critical + t.high));
    return Math.max.apply(null, [1].concat(values));
  }, [chartRows]);

  const yAxisTicks = useMemo(() => buildYAxisTicks(chartMax), [chartMax]);

  const insightPaths = useMemo(() => buildSparklinePaths(chartRows, chartMax, 520, 170), [chartRows, chartMax]);
  const trendPaths = useMemo(() => buildSparklinePaths(chartRows, chartMax, 420, 170), [chartRows, chartMax]);

  const axisLabels = useMemo(() => {
    if (!chartRows.length) return [];

    const maxLabels =
      rangeLabel === "365" ? 12 :
      rangeLabel === "90" ? 5 :
      rangeLabel === "30" ? 6 :
      7;

    const step = Math.max(1, Math.ceil(chartRows.length / maxLabels));

    return chartRows
      .map((row, idx) => ({ ...row, idx }))
      .filter((_, idx) => idx === 0 || idx === chartRows.length - 1 || idx % step === 0);
  }, [chartRows, rangeLabel]);

  const insightLines = useMemo(() => {
    const categoryCounts: Record<string, number> = {};

    chartFeed.forEach((item) => {
      const key = titleCase(item.category || item.signal_category || item.signal_label || item.trigger || "");
      if (!key) return;
      categoryCounts[key] = (categoryCounts[key] || 0) + 1;
    });

    return Object.keys(categoryCounts)
      .map((key) => [key, categoryCounts[key]] as [string, number])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map((entry) => entry[0]);
  }, [chartFeed]);

  const liveSignals = useMemo(() => {
    return chartFeed.slice(0, 8).map((f) => ({
      label: titleCase(f.category || f.signal_category || f.signal_label || f.trigger || "signal"),
      severity: severityClass(f.severity),
    }));
  }, [chartFeed]);

  const selectedSchoolSignals = useMemo(() => {
    const labels = chartFeed
      .slice(0, 3)
      .map((f) => titleCase(f.category || f.signal_category || f.signal_label || f.trigger || ""))
      .filter(Boolean);

    if (labels.length) return `${labels.join(" • ")} spike`;
    return topSchool.events > 0 ? "Support signals elevated" : "No linked signals";
  }, [chartFeed, topSchool.events]);

  const scopedActiveSignals = chartFeed.length || topSchool.events || 0;
  const stormProbability = scoreToStormProbability(safeNum(topSchool.score, 0));
  const wellnessScore = stormProbability;

  const summaryCards = useMemo(
    () => [
      { label: "Active Signals", value: scopedActiveSignals.toLocaleString() },
      { label: "Flagged Schools", value: topSchool.school_name === "No Data" ? "0" : String(visibleSchools.length || 1) },
      { label: "Wellness", value: `${wellnessScore}%` },
      { label: "Crisis Watch", value: titleCase(topSchool.status) },
      { label: "Response", value: fmtPercent(summary.advisor_response) },
    ],
    [scopedActiveSignals, topSchool.school_name, topSchool.status, wellnessScore, summary.advisor_response, visibleSchools.length]
  );

  const districtLabel = useMemo(() => {
    const schoolCount = visibleSchools.length;
    return schoolCount === 1 ? "1 School Network" : `${schoolCount} School Network`;
  }, [visibleSchools.length]);

  const pulseAnchor = useMemo(() => {
    const topIndex = Math.max(0, visibleSchools.findIndex((s) => s.school_name === topSchool.school_name));
    return {
      x: 24 + ((topIndex * 15) % 22),
      y: 24 + ((topIndex * 8) % 18),
    };
  }, [visibleSchools, topSchool.school_name]);

  const mapNodes = useMemo(() => {
    const positions = [
      { x: 24, y: 32 }, { x: 30, y: 46 }, { x: 44, y: 38 }, { x: 60, y: 48 },
      { x: 72, y: 34 }, { x: 22, y: 58 }, { x: 82, y: 48 }, { x: 66, y: 28 },
      { x: 14, y: 42 }, { x: 52, y: 58 },
    ];

    return visibleSchools.slice(0, positions.length).map((school, idx) => ({
      ...school,
      x: school.school_name === topSchool.school_name ? pulseAnchor.x : positions[idx].x,
      y: school.school_name === topSchool.school_name ? pulseAnchor.y : positions[idx].y,
      tone: schoolToneClass(school.status),
      selected: school.school_name === topSchool.school_name,
    }));
  }, [visibleSchools, topSchool.school_name, pulseAnchor.x, pulseAnchor.y]);

  const selectedPulseClass = severityClass(topSchool.status);

  const mapBackdropStyle = useMemo(
    () =>
      ({
        ["--aura-map-image" as any]: `url(${auraWorldMapBg})`,
        ["--aura-sweep-size" as any]: "500px",
      } as React.CSSProperties),
    []
  );

  const exportAuraSnapshot = async () => {
    try {
      const days = rangeDays(rangeLabel);

      const response = await api.get("/admin/aura/report.csv", {
        params: {
          days,
          continent: selectedContinent !== "ALL" ? selectedContinent : undefined,
          region: selectedRegion !== "ALL" ? selectedRegion : undefined,
          country: selectedCountry !== "ALL" ? selectedCountry : undefined,
          district: selectedDistrict !== "ALL" ? selectedDistrict : undefined,
          school: selectedSchool !== "ALL" ? selectedSchool : undefined,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const dateStamp = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.download = `AURA_Snapshot_${rangeText(rangeLabel).replace(/\s+/g, "_")}_${dateStamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e);
      setErr(
        safeDisplayText(
          e?.response?.data?.detail ||
            e?.response?.data ||
            e?.message ||
            "Unable to export AURA snapshot."
        )
      );
    }
  };

  return (
    <div className="aura-premium-page">
      <section className="aura-premium-shell">
        <ErhAlertPopup role="admin" openRoute={(incidentId) => `/admin/erh/${encodeURIComponent(incidentId)}`} title="AURA ERH™ — App Administration Emergency Watch" />
        <header className="aura-premium-header">
          <div className="aura-brand-block">
            <div className="aura-brand-mark">
              <div className="aura-brand-ring" />
              <div className="aura-brand-word">AURA</div>
            </div>
            <div className="aura-title-wrap">
              <div className="aura-title">AURA EMOTIONAL WEATHER MAP</div>
              <div className="aura-subtitle">Mapping emotional climate patterns and safety signals across the globe</div>
            </div>
          </div>

          <div className="aura-header-status">
            <div className="aura-avatar" />
            <div className="aura-header-status-copy">
              <div className="aura-header-label">{titleCase(viewMode)}</div>
              <div className="aura-header-value">{districtLabel}</div>
            </div>
          </div>
        </header>

        <section className="aura-toolbar-row">
          <div className="aura-toolbar-left">
            <select
              className="aura-select"
              value={selectedContinent}
              onChange={(e) => {
                setSelectedContinent(e.target.value);
                setSelectedRegion("ALL");
                setSelectedCountry("ALL");
                setSelectedDistrict("ALL");
                setSelectedSchool("ALL");
                setViewMode("region");
              }}
            >
              <option value="ALL">All Continents</option>
              {continentOptions.map((continent) => (
                <option key={continent} value={continent}>{continent}</option>
              ))}
            </select>

            <select
              className="aura-select"
              value={selectedRegion}
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setSelectedCountry("ALL");
                setSelectedDistrict("ALL");
                setSelectedSchool("ALL");
                setViewMode("region");
              }}
            >
              <option value="ALL">All Regions</option>
              {regionOptions.map((region) => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>

            <select
              className="aura-select"
              value={selectedCountry}
              onChange={(e) => {
                const nextCountry = e.target.value;
                const geo = getGeoForCountry(nextCountry);

                setSelectedCountry(nextCountry);

                if (nextCountry !== "ALL") {
                  if (geo.continent) setSelectedContinent(geo.continent);
                  if (geo.region) setSelectedRegion(geo.region);
                }

                setSelectedDistrict("ALL");
                setSelectedSchool("ALL");
                setViewMode("nation");
              }}
            >
              <option value="ALL">All Countries</option>
              {countryOptions.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            <select
              className="aura-select"
              value={selectedDistrict}
              onChange={(e) => {
                setSelectedDistrict(e.target.value);
                setSelectedSchool("ALL");
                setViewMode("district");
              }}
            >
              <option value="ALL">All Districts</option>
              {districtOptions.map((district) => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>

            <select
              className="aura-select aura-select-wide"
              value={selectedSchool}
              onChange={(e) => {
                setSelectedSchool(e.target.value);
                setViewMode("school");
              }}
            >
              <option value="ALL">All Schools</option>
              {schoolOptions.map((school) => (
                <option key={school} value={school}>{school}</option>
              ))}
            </select>
          </div>

          <div className="aura-toolbar-right">
            <div className="aura-date-pill">{rangeText(rangeLabel)}</div>
            <div className="aura-segmented aura-segmented-tight">
              {[
                { key: "7", label: "PAST 7 DAYS" },
                { key: "30", label: "PAST 30 DAYS" },
                { key: "90", label: "PAST 90 DAYS" },
                { key: "365", label: "PAST 12 MONTHS" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={"aura-segment " + (rangeLabel === item.key ? "active" : "")}
                  onClick={() => setRangeLabel(item.key as RangeLabel)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {err ? <div className="aura-error-banner">{safeDisplayText(err)}</div> : null}

        <section className="aura-premium-grid">
          <aside className="aura-left-rail">
            <div className="aura-rail-section-title">School Climate Radar</div>

            <div className="aura-school-stack">
              {visibleSchools.length ? (
                visibleSchools.slice(0, 7).map((school, idx) => {
                  const top = school.school_name === topSchool.school_name;
                  return (
                    <div key={`${school.school_name}-${idx}`} className={"aura-school-card " + schoolToneClass(school.status) + " " + (top ? "featured" : "")}>
                      <div className="aura-school-card-main">
                        <div className="aura-school-card-name">{school.school_name}</div>
                        <div className={"aura-school-card-badge " + schoolToneClass(school.status)}>
                          {titleCase(school.status)}
                        </div>
                      </div>
                      {top ? (
                        <div className="aura-school-card-detail">
                          <div>{selectedSchoolSignals}</div>
                          <div className="aura-school-card-probability">{stormProbability}%</div>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <div className="aura-empty-rail">No school-linked data yet.</div>
              )}
            </div>

            <div className="aura-filter-panel">
              <div className="aura-rail-section-title">Threat Signals</div>
              <div className="aura-filter-grid">
                {["Anxiety", "Bullying", "Family Stress", "Isolation", "Depression", "Burnout"].map((item) => (
                  <div key={item} className="aura-filter-item">
                    <span className="aura-filter-check">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="aura-rail-section-title aura-rail-section-gap">Safe Signals</div>
              <div className="aura-filter-grid">
                {["Help Seeking", "Advisor Engagement", "Peer Support", "Positive Coping"].map((item) => (
                  <div key={item} className="aura-filter-item safe">
                    <span className="aura-filter-check">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <main className="aura-main-stage">
            <section className="aura-summary-row">
              {summaryCards.map((card) => (
                <div key={card.label} className="aura-summary-card">
                  <div className="aura-summary-label">{card.label}</div>
                  <div className="aura-summary-value">{card.value}</div>
                </div>
              ))}
            </section>

            <section className="aura-cinematic-map">
              <div className="aura-map-backdrop" style={mapBackdropStyle}>
                <div className="aura-map-image-layer" />
                <div className="aura-map-image-vignette" />
                <div className="aura-map-image-glow" />
                <div className="aura-grid-lines" />
                <div className="aura-orbit aura-orbit-1" />
                <div className="aura-orbit aura-orbit-2" />
                <div className="aura-map-stars" />

                <div className="aura-hot-zone" style={{ left: pulseAnchor.x + "%", top: pulseAnchor.y + "%" }} />
                <div className="aura-radar-sweep" style={{ left: pulseAnchor.x + "%", top: pulseAnchor.y + "%" }} />

                {mapNodes.map((node) => (
                  <div
                    key={node.school_name}
                    className={"aura-map-node " + node.tone + " " + (node.selected ? "selected" : "")}
                    style={{ left: node.x + "%", top: node.y + "%" }}
                    title={`${node.school_name} • ${node.status}`}
                  >
                    <span className="aura-map-node-core" />
                  </div>
                ))}

                <div className={"aura-pulse-ring " + selectedPulseClass} style={{ left: pulseAnchor.x + "%", top: pulseAnchor.y + "%" }} />
                <div className={"aura-pulse-ring aura-pulse-ring-delay " + selectedPulseClass} style={{ left: pulseAnchor.x + "%", top: pulseAnchor.y + "%" }} />

                <div className="aura-callout-premium">
                  <div className="aura-callout-school">{topSchool.school_name}</div>
                  <div className={"aura-callout-status " + schoolToneClass(topSchool.status)}>
                    {titleCase(topSchool.status)}
                  </div>
                  <div className="aura-callout-probability">Storm Probability {stormProbability}%</div>
                  <div className="aura-callout-detail">{selectedSchoolSignals}</div>
                </div>
              </div>

              <div className="aura-map-legend">
                <span className="legend-dot low" /> LOW
                <span className="legend-dot moderate" /> MODERATE
                <span className="legend-dot elevated" /> ELEVATED
                <span className="legend-dot critical" /> CRITICAL
                <span className="legend-stat">{scopedActiveSignals.toLocaleString()} SIGNALS</span>
              </div>
            </section>

            <section className="aura-bottom-row">
              <div className="aura-insight-panel">
                <div className="aura-panel-title">AURA INSIGHT</div>

                <div className="aura-chart-legend">
                  <span className="aura-chart-legend-item"><span className="legend-line blue" /> School Risk Trend</span>
                  <span className="aura-chart-legend-item"><span className="legend-line red" /> Incident Volume</span>
                  <span className="aura-chart-legend-item"><span className="legend-line gold" /> Critical / High Risk</span>
                </div>

                <div className="aura-insight-content">
                  <div className="aura-insight-list">
                    {insightLines.length ? (
                      insightLines.map((line) => (
                        <div key={line} className="aura-insight-item">
                          <span className="aura-insight-check">✓</span>
                          <span>{line}</span>
                          <span className="aura-insight-alert">▲</span>
                        </div>
                      ))
                    ) : (
                      <div className="aura-empty-rail">No incidents for this range.</div>
                    )}
                  </div>

                  <div className="aura-mini-chart">
                    <svg viewBox="0 0 560 210" preserveAspectRatio="none">
                      <text x="4" y="14" fill="#9fb6d8" fontSize="12">Y: risk / incidents</text>
                      <text x="430" y="205" fill="#9fb6d8" fontSize="12">X: reporting period</text>

                      {yAxisTicks.map((tick) => {
                        const y = 30 + ((Math.max(...yAxisTicks) - tick) / Math.max(1, Math.max(...yAxisTicks))) * 160;
                        return (
                          <React.Fragment key={`insight-${tick}`}>
                            <text x="4" y={y + 4} fill="#9fb6d8" fontSize="11">{tick}</text>
                            <line x1="40" x2="550" y1={y} y2={y} className="aura-chart-grid" />
                          </React.Fragment>
                        );
                      })}

                      {insightPaths.score ? <path d={insightPaths.score} fill="none" stroke="#57d6ff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" transform="translate(40,20)" /> : null}
                      {insightPaths.events ? <path d={insightPaths.events} fill="none" stroke="#ff7a66" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" transform="translate(40,20)" /> : null}
                      {insightPaths.critical ? <path d={insightPaths.critical} fill="none" stroke="#f9c85b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" transform="translate(40,20)" /> : null}
                    </svg>
                  </div>
                </div>
              </div>

              <div className="aura-trend-panel">
                <div className="aura-panel-title">{topSchool.school_name} - Trend Window</div>

                <div className="aura-chart-legend">
                  <span className="aura-chart-legend-item"><span className="legend-line blue" /> School Risk Trend</span>
                  <span className="aura-chart-legend-item"><span className="legend-line gold" /> Incident Volume</span>
                </div>

                <div className="aura-trend-chart">
                  <svg viewBox="0 0 460 210" preserveAspectRatio="none">
                    <text x="4" y="14" fill="#9fb6d8" fontSize="12">Y: risk / incidents</text>
                    <text x="320" y="205" fill="#9fb6d8" fontSize="12">X: reporting period</text>

                    {yAxisTicks.map((tick) => {
                      const y = 30 + ((Math.max(...yAxisTicks) - tick) / Math.max(1, Math.max(...yAxisTicks))) * 160;
                      return (
                        <React.Fragment key={`trend-${tick}`}>
                          <text x="4" y={y + 4} fill="#9fb6d8" fontSize="11">{tick}</text>
                          <line x1="40" x2="450" y1={y} y2={y} className="aura-chart-grid" />
                        </React.Fragment>
                      );
                    })}

                    {trendPaths.score ? <path d={trendPaths.score} fill="none" stroke="#57d6ff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" transform="translate(40,20)" /> : null}
                    {trendPaths.events ? <path d={trendPaths.events} fill="none" stroke="#f9c85b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" transform="translate(40,20)" /> : null}
                  </svg>
                </div>

                <div className="aura-trend-axis">
                  {axisLabels.map((row) => (
                    <span key={`${row.fullLabel}-${row.idx}`}>{row.label}</span>
                  ))}
                </div>
              </div>
            </section>

            <section className="aura-live-ticker">
              <div className="aura-live-label">LIVE SIGNALS:</div>

              <div className="aura-live-items">
                {liveSignals.length ? (
                  liveSignals.map((item, idx) => (
                    <span key={`${item.label}-${idx}`} className={"aura-live-pill " + item.severity}>
                      {item.label}
                    </span>
                  ))
                ) : (
                  <span className="aura-live-pill low">No recent signals</span>
                )}
              </div>

              <button type="button" className="aura-export-btn" onClick={exportAuraSnapshot}>
                EXPORT CSV
              </button>
            </section>
          </main>
        </section>

        {loading ? <div className="aura-loading-bar">Refreshing AURA…</div> : null}
      </section>
    </div>
  );
}